import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import '../../../assets/css/common.css';
import { Grid, Row, Col, FormGroup, ControlLabel, FormControl } from 'react-bootstrap'
import { CustomDate } from '../../../components/CustomControls/CustomDateTime';
import moment from 'moment';
import InventoryReleaseService from '../../../api/Inventory/InventoryReleaseService';
import { useTranslation } from 'react-i18next';
import AutoCompletation from '../../../components/Common/AutoCompletation';
import { useFormModal } from '../../../components/Modals/formModal.js/FormModal';
import ReleaseList from './ReleaseList';
import { Add, Remove } from '../../../components/Icons/Icons';
import { Tooltip } from '../../../components/Utils/Tooltips';
import {
    buildMedicationLabel,
    isAddItemEnabled,
    MAX_LOT_NUMBER_LENGTH,
    MAX_MOVE_ITEM_QUANTITY,
    resolveEffectiveExpiration,
    resolveEffectiveLot,
    resolveExpirationValue,
    resolveSubmittedRelease
} from './inventoryFormRules';
import { QuantityInput } from './QuantityInput';


export const InventoryForm = forwardRef(({ handleSubmit,
    handleSearchItem,
    disabled,
    itemPlaceholder,
    useSelecteableRelease,
    assignmentType, assignId }, ref) => {
    const { t } = useTranslation()
    const medicationRef = useRef();
    const lotRef = useRef();
    const expirationDateInputRef = useRef();
    const expirationDateRef = useRef();
    const quantityRef = useRef();

    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [typedLot, setTypedLot] = useState('');
    const [typedExpiration, setTypedExpiration] = useState(null);
    const [expirationText, setExpirationText] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [ReleaseModal, ReleaseActions] = useFormModal({ FormContent: ReleaseList })

    const effectiveLot = resolveEffectiveLot(selectedRelease, typedLot);
    const effectiveExpiration = resolveEffectiveExpiration(selectedRelease, typedExpiration);
    const isSelectedReleaseStored = Number(selectedRelease?.id) > 0;
    // CustomDate formats a Date but re-parses a DD/MM/YYYY string as ISO, so the
    // typed text is only shown while it is still incomplete.
    const visibleExpiration = isSelectedReleaseStored
        ? (selectedRelease?.expirationDate ?? '')
        : (typedExpiration ?? expirationText);

    useImperativeHandle(ref, () => ({
        cleanForm: () => handleItemClean()
    }));

    useEffect(() => {
        if (selectedItem != null) {
            if (useSelecteableRelease == false) lotRef.current.focus()
        }
    }, [selectedItem == null])

    async function fetchItems(value) {
        const result = await handleSearchItem(value);
        const hasExactMatch = result.some(item => item.match === 'Exact');
        if (hasExactMatch) {
            const filteredResult = result.filter(item => item.match === 'Exact');
            const item = filteredResult[0];
            medicationRef.current.setInputValue(item);
            setSelectedItem(item);
            return [];
        }

        return result;
    }

    function handleItemClean() {
        setSelectedItem(null)
        setSelectedRelease(null)
        setQuantity(1)
        setTypedLot('')
        setTypedExpiration(null)
        setExpirationText('')
        medicationRef.current.clear()
        if (useSelecteableRelease == false)
            lotRef.current.clear()
    }

    async function fetchReleases(value) {
        if (isNewItem()) return []

        const result = await InventoryReleaseService.Find({
            page: 1,
            pageSize: 10,
            itemId: selectedItem.inventoryItemId,
            search: value,
            includeStock: true,
            includeExpired: true
        })

        return result.Values
    }
    const isNewItem = () => (selectedItem != null && (selectedItem.inventoryItemId == undefined || selectedItem.inventoryItemId < 1))

    function isFormValid() {
        return isAddItemEnabled({
            hasSelectedItem: selectedItem !== null,
            lotNumber: effectiveLot,
            expirationDate: effectiveExpiration,
            quantity: quantity,
            maxQuantity: MAX_MOVE_ITEM_QUANTITY
        })
    }

    const onHandleExpirationChange = (e, v) => {
        const date = resolveExpirationValue(v);
        setTypedExpiration(date)
        setExpirationText(date != null ? moment(date).format('DD/MM/YYYY') : (typeof v === 'string' ? v : ''))
        if (date == null) return;

        expirationDateRef.current._closeCalendar()
        setFocus(quantityRef.current)
    }

    function handleFormSubmit(e) {
        e.preventDefault();

        if (!isFormValid()) return
        handleSubmit({
            selectedItem: selectedItem,
            selectedRelease: resolveSubmittedRelease(selectedRelease, effectiveLot, effectiveExpiration),
            quantity: quantity
        })
        handleItemClean()
        setFocus(medicationRef.current)
    }


    function setFocus(input) {
        input.disabled = false
        input.focus()
    }

    const curringSelected = (callback) => {
        return (selected) => {
            if (selected != null && selected.length > 0) callback(selected[0])
            else callback(null)
        }
    }

    const handleShowReleaseModal = async () => {
        await ReleaseActions.Show({
            title: t('inventory.commons.lotNumber'),
            onConfirmation: (selected) => setSelectedRelease(selected),
            initValue: { itemId: selectedItem.inventoryItemId, assignmentType: assignmentType, assignId: assignId, includeExpired: true }
        })
    }

    const onCleanSelectedRelease = () => {
        setSelectedRelease(null)
        setQuantity(0)
    }

    const releaseSelectation = () => useSelecteableRelease ?
        (<>
            {selectedRelease == null && <div className='input-group-container'>
                <FormGroup style={{ width: "100%" }}>
                    <button className="btn btn-primary" disabled={selectedItem == null} onClick={_ => handleShowReleaseModal()}>
                        <Tooltip>
                            <Add /> {t('inventory.dispense.selectLotNumber')}
                        </Tooltip>
                    </button>
                </FormGroup>
            </div>
                || <div className='input-group-container'>
                    <FormGroup style={{ width: "100%" }}>
                        <input disabled={true} value={selectedRelease?.lotNumber} className="rbt-input-main form-control rbt-input" />
                    </FormGroup>

                    <FormGroup style={{ width: "100%" }} >
                        <CustomDate
                            dateRef={expirationDateRef}
                            inputRef={expirationDateInputRef}
                            name={`expirationDate`}
                            onChange={() => { }}
                            onBlur={() => { }}
                            value={selectedRelease?.expirationDate ?? null}
                            defaulValue={null}
                            dateFormat="DD/MM/YYYY"
                            disabled={true}
                            inputProps={{ placeholder: t('commons.expirationDate') }}
                        />
                    </FormGroup>

                    <FormGroup style={{ width: "100%" }}>
                        <button className="btn btn-danger" disabled={selectedRelease == null} onClick={onCleanSelectedRelease}>
                            <Tooltip tooltip={t('commons.clear')} id={"tooltipedit-limpiarpaciente"}>
                                <span aria-hidden="true"><Remove /></span>
                            </Tooltip>
                        </button>
                    </FormGroup>
                </div>}
        </>
        ) : (<div className='input-group-container'>
            <FormGroup style={{ width: "100%" }}>
                <AutoCompletation
                    id={'Lote'}
                    ref={lotRef}
                    labelKey={(option) => {
                        return `L: ${option.lotNumber} - V: ${moment(option.expirationDate).format('DD-MM-YYYY')} - stock: ${option.stockQuantity}`;
                    }}
                    disabled={selectedItem == null}
                    fetchOptions={fetchReleases}
                    inputProps={{ maxLength: MAX_LOT_NUMBER_LENGTH }}
                    placeholder={t('inventory.commons.lotNumber')}
                    onInputChange={(text) => setTypedLot(text)}
                    onChange={(e) => {
                        curringSelected(setSelectedRelease)(e)
                        if (e != null && e.id > 0) setFocus(quantityRef.current)
                    }}
                    onBack={() => medicationRef.current.focus()}
                />
            </FormGroup>

            <FormGroup style={{ width: "100%" }} >
                <CustomDate
                    dateRef={expirationDateRef}
                    inputRef={expirationDateInputRef}
                    name={`expirationDate`}
                    onChange={onHandleExpirationChange}
                    onBlur={() => { }}
                    value={visibleExpiration}
                    dateFormat="DD/MM/YYYY"
                    disabled={selectedItem == null || (selectedRelease && selectedRelease.id > 0) || disabled}
                    inputProps={{ placeholder: t('commons.expirationDate') }}
                />
            </FormGroup></div>)


    return (<form onSubmit={handleFormSubmit} autoComplete="off">
        <FormGroup>
            <AutoCompletation
                id={'medication'}
                ref={medicationRef}
                labelKey={(option) => buildMedicationLabel(option)}
                placeholder={itemPlaceholder ?? t('inventory.commons.searchMedication')}
                fetchOptions={fetchItems}
                onChange={curringSelected(setSelectedItem)}
                onNext={() => { if (useSelecteableRelease == false) lotRef.current.focus() }}
            />
        </FormGroup>
        <hr />
        {releaseSelectation()}
        <hr />
        <div style={{ display: "flex", justifyContent: 'end' }} >
            <div style={{ display: 'flex', flexDirection: "column", width: "25%", marginRight: '1rem' }}>
                <QuantityInput ref={quantityRef}
                    value={quantity}
                    onChange={setQuantity}
                    max={MAX_MOVE_ITEM_QUANTITY}
                    disabled={disabled}
                    placeholder={t('inventory.dispense.quantity')} />
            </div>
            <div style={{ alignSelf: 'self-end' }}>
                <button type="submit" className='btn btn-success' disabled={!isFormValid()}>{t('buttons.add')}</button>
            </div>
        </div>
        <ReleaseModal />
    </form>)
})